import { basketGroupingsLocale } from '@epicuro-next/constants/features/localization';
import {
  CBasketConfigurationKeys,
  CBASKET_NAMESPACE,
} from '@epicuro-next/data/baskets';
import {
  BasketPropertyBag,
  ExtendedDtoBasket,
  Item,
  ReferencedProduct,
} from '@epicuro-next/data/baskets-dto';
import {
  getBasketSummary,
  getSelectedShippingOptionId,
  getShippingOptions,
  mapDtoStatus,
  sortGroupedBasket,
} from '@epicuro-next/data/baskets-grouping';
import {
  BasketParkingOptionProperty,
  BasketParkingOptions,
} from '@epicuro-next/models/basket';
import {
  getValueFromConfigState,
  selectConfigurationsState,
} from '@epicuro-next/platform/configuration';
import {
  selectAccountIncoterm,
  selectAccountParkingDate,
  selectAccountParkingReason,
  selectActiveAccountDropShipAvailable,
} from '@epicuro-next/state/accounts-state';
import { selectRouteParams } from '@epicuro-next/state/router-store';
import {
  isAddressDeleted,
  isNonNullish,
  isNullish,
  memoize,
  pick,
  uniqueByPropertyPath,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import {
  Basket,
  BasketColumnKeys,
  BasketQuantity,
  BasketRow,
  BasketRowsGroupType,
  BasketStatus,
  DeliveryWindowType,
  DiscountBasketColumn,
  OrderType,
  ReviewStatus,
  Total,
} from 'graphql-api/baskets-api-interfaces';
import {
  BasketAddressRowsGroupDetails,
  BasketRowsGroup,
  Product,
} from 'graphql-api/gql-api-interfaces';
import {
  aggregateTotals,
  getSizesTotal,
} from 'libs/data/baskets-grouping/src/lib/mappings';
import { getDiscountsType } from 'libs/data/baskets-grouping/src/lib/shared/get-discounts-type';

import {
  basketConfigurationsDefaultValues,
  CBasketConfigurations,
} from '../../configurations';
import { CanBasketBeChangedChecks, IGroupingOption } from '../../models';
import { CanBasketBeChanged } from '../../models/can-basket-be-changed-checks.models';
import {
  countArticlesOccurrences,
  getBasketAddressGroupLines,
  getBasketBrandGroupLines,
  getBasketGroupLines,
  getBasketLinesEntity,
  getBasketOrderNumberGroupLines,
  getBasketRowLines,
  getBasketStatusToShow,
  isBackOrderBasketItem,
  isBasketStatusNewish,
} from '../../shared';
import {
  isFutureBasketItem,
  isFutureDtoBasketItem,
  isNowBasketItem,
  isNowDtoBasketItem,
} from '../../shared/is-basket-item-of-order-type/is-basket-item-of-order-type';
import {
  adapter,
  basketItemReducer,
  ExtendedBasketItem,
  State,
} from '../reducers/basket-data.reducer';

import {
  selectDisableEditBasketOnReviewRule,
  selectGroupByOptionsRule,
  selectIsShippingOptionEnabledRule,
  selectRestrictMixedBasketDropShipRule,
  selectShipInFullEnabledRule,
} from './basket-configuration-rules.selectors';
import {
  selectActiveGroupingType,
  selectActiveSortingDirection,
  selectActiveSortingType,
} from './basket-options.selectors';
import {
  selectBasketDataState,
  selectGroupedBasketEntities,
} from './feature.selectors';

export const { selectEntities: selectDtoBasketEntities, selectIds } =
  adapter.getSelectors(selectBasketDataState);

/*
 * Selects the active basket ID
 */
export const selectActiveBasketId = createSelector(
  selectBasketDataState,
  (state: State) => state?.activeBasketId,
);

export const selectHasActiveBasketId = createSelector(
  selectActiveBasketId,
  (activeBasketId) => {
    return Boolean(activeBasketId);
  },
);

export const selectLoadedBasketIds = createSelector(selectIds, (ids) =>
  ids.map((id) => id.toString()),
);
/*
 * Selects the currently opened basket ID
 */
export const selectOpenedBasketId = createSelector(
  selectRouteParams,
  selectActiveBasketId,
  (routeParams, activeBasketId) =>
    (routeParams?.basketId ?? activeBasketId) as string,
);

export const selectDtoBasketFromEntities = createSelector(
  selectBasketDataState,
  selectOpenedBasketId,
  (state, basketId) => {
    const entity = state.entities[basketId];

    return entity ?? null;
  },
);

export const selectAllDtoBasketsFromEntitiesByBasketId = createSelector(
  selectBasketDataState,
  (state) => {
    return state.entities;
  },
);

export const selectOpenedDtoBasketFromEntity = createSelector(
  selectOpenedBasketId,
  selectDtoBasketEntities,
  (basketId, entities) => entities[basketId],
);

export const selectOpenedDtoBasketETag = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basket) => basket?.etag,
);

export const selectIsReadOnlyDtoBasketFlag = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basket) => basket?.isReadOnly,
);

export const selectActiveBasketFromEntities = createSelector(
  selectDtoBasketEntities,
  selectActiveBasketId,
  (entities, activeBasketId) => {
    if (!activeBasketId) return null;

    return entities[activeBasketId];
  },
);

export const selectActiveBasketSummary = createSelector(
  selectActiveBasketFromEntities,
  (entity) => {
    return entity ? getBasketSummary(entity) : null;
  },
);

export const selectOpenedBasketSummary = createSelector(
  selectDtoBasketFromEntities,
  (entity: ExtendedDtoBasket | null) => {
    return entity ? getBasketSummary(entity) : null;
  },
);

export const selectBasketDtoFromEntitiesById = (basketId: string) => {
  return createSelector(
    selectDtoBasketEntities,
    (entities) => entities[basketId] ?? null,
  );
};

export const selectGroupedBasketsEntity = createSelector(
  selectGroupedBasketEntities,
  selectOpenedBasketId,
  (entities, basketId) => entities[basketId] ?? null,
);

/**
 * Selects grouped basket entity data
 */

export const selectGroupedBasketData = createSelector(
  selectGroupedBasketsEntity,
  selectActiveGroupingType,
  (entity, groupType) => entity?.[groupType] ?? null,
);

/**
 * Selects basket line sorted data
 */
export const selectBasketData = createSelector(
  selectGroupedBasketData,
  selectActiveSortingType,
  selectActiveSortingDirection,
  (groupedBasket, sortType, sortDirection) => {
    return groupedBasket
      ? sortGroupedBasket(groupedBasket, sortType, sortDirection)
      : null;
  },
);

export const selectBasketLineEntities = createSelector(
  selectBasketData,
  (basket) => (basket ? getBasketLinesEntity(basket) : {}),
);

export const selectBasketLineItemById = (uniqueId: string) =>
  createSelector(selectBasketLineEntities, (entity) => entity[uniqueId]);

export const selectBasketLinesList = createSelector(
  selectBasketLineEntities,
  (lines) => Object.values(lines),
);

export const selectBasketRowLines = createSelector(
  selectBasketLinesList,
  getBasketRowLines,
);

export const selectDiscountPercentageByBasketLineId = createSelector(
  selectBasketRowLines,
  (basketLineItems) => {
    const discountPercentageByBasketLineId: {
      [basketLineId: string]: number | undefined;
    } = {};

    basketLineItems.forEach((basketLineItem) => {
      const discountPercentageColumn = basketLineItem.data.columns?.find(
        (c) => c.key === BasketColumnKeys.DiscountPercentage,
      ) as DiscountBasketColumn;

      const discountRate =
        discountPercentageColumn?.discount?.percentage ?? undefined;

      basketLineItem.basketItemIds.forEach((basketItemId) => {
        discountPercentageByBasketLineId[basketItemId] = discountRate;
      });
    });

    return discountPercentageByBasketLineId;
  },
);

export const selectGreatestDiscountPercentageOfCloseOutProductRow =
  createSelector(selectBasketRowLines, (basketLineItems) => {
    const percentages = basketLineItems
      .filter(
        (lineItem) => (lineItem.data as BasketRow).product.isclosedoutproduct,
      )
      .map(
        (lineItem) =>
          (
            lineItem.data.columns.find(
              (column) => column.key === BasketColumnKeys.DiscountPercentage,
            ) as DiscountBasketColumn
          ).discount?.percentage ?? 0,
      );
    return Math.max(...percentages);
  });

export const selectBasketGroupLines = createSelector(
  selectBasketLinesList,
  getBasketGroupLines,
);

export const selectBasketAddressGroupLines = createSelector(
  selectBasketGroupLines,
  getBasketAddressGroupLines,
);

export const selectBasketBrandGroupLines = createSelector(
  selectBasketGroupLines,
  getBasketBrandGroupLines,
);

export const selectBasketOrderNumberGroupLines = createSelector(
  selectBasketGroupLines,
  getBasketOrderNumberGroupLines,
);

/**
 * Selects the basket totals
 */
export const selectBasketTotals = createSelector(
  selectBasketData,
  (basketData: Basket) => basketData?.total,
);

export const selectBasketBulkTotals = createSelector(
  selectBasketData,
  (basketData: Basket) => basketData?.bulkTotal,
);

export const selectBasketBulkQuantity = createSelector(
  selectBasketBulkTotals,
  (bulkTotal) => bulkTotal?.requested.quantity || 0,
);

export const selectHasBasketBulkItems = createSelector(
  selectBasketBulkQuantity,
  (bulkQuantity) => Boolean(bulkQuantity),
);

export const selectBasketRealTotals = createSelector(
  selectBasketData,
  (basketData: Basket) => basketData?.realTotal,
);

export const selectBasketRealRequestedQuantity = createSelector(
  selectBasketRealTotals,
  (realTotal) => realTotal?.requested.quantity || 0,
);

export const selectBasketRealConfirmedQuantity = createSelector(
  selectBasketRealTotals,
  (realTotal) => realTotal?.confirmed?.quantity || 0,
);

/**
 * Selects the totals displayed in the navigation menu
 */
export const selectActiveBasketTotals = createSelector(
  selectActiveBasketSummary,
  (basketData) => basketData?.total,
);

export const selectOpenedBasketTotals = createSelector(
  selectOpenedBasketSummary,
  (basketData) => basketData?.total,
);

export const selectActiveBasketCurrency = createSelector(
  selectActiveBasketTotals,
  (totals) => totals && totals.requested.currency,
);

export const selectBasketReviewStatus = createSelector(
  selectBasketData,
  (basketData) => basketData?.reviewStatus ?? null,
);

export const selectIsBasketReviewNeeded = createSelector(
  selectBasketData,
  (basketData) => basketData?.reviewNeeded ?? false,
);

export const selectIsBasketFrozenReviewState = createSelector(
  selectBasketReviewStatus,
  selectIsBasketReviewNeeded,
  (reviewStatus, reviewNeeded) =>
    Boolean(reviewNeeded && reviewStatus === ReviewStatus.Pending),
);

export const selectIsBasketFrozenReviewStateByRule = createSelector(
  selectBasketReviewStatus,
  selectDisableEditBasketOnReviewRule,
  (reviewStatus, disableEditBasketOnReviewRule) =>
    Boolean(
      disableEditBasketOnReviewRule && reviewStatus === ReviewStatus.Pending,
    ),
);

export const selectBasketStatus = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketDto: ExtendedDtoBasket) => mapDtoStatus(basketDto?.status),
);

export const selectIsBasketStatusProcessed = createSelector(
  selectBasketStatus,
  (status) => status === BasketStatus.Processed,
);

export const selectIsBasketStatusNewish = createSelector(
  selectBasketStatus,
  isBasketStatusNewish,
);

export const selectIsBasketStatusNotNew = createSelector(
  selectBasketStatus,
  selectIsBasketStatusNewish,
  (status, isNewish) => status && !isNewish,
);

export const selectBasketStatusToShow = createSelector(
  selectBasketStatus,
  selectBasketReviewStatus,
  getBasketStatusToShow,
);

export const selectIsActiveBasketAndOpenedBasketDifferent = createSelector(
  selectActiveBasketId,
  selectOpenedBasketId,
  (activeBasketId, openedBasketId) =>
    Boolean(openedBasketId && activeBasketId !== openedBasketId),
);

export const selectOpenedOrActiveBasket = createSelector(
  selectIsActiveBasketAndOpenedBasketDifferent,
  selectIsBasketStatusNewish,
  selectOpenedDtoBasketFromEntity,
  selectActiveBasketFromEntities,
  (isOpenedDifferent, basketStatusNewish, openedBasket, activeBasket) =>
    isOpenedDifferent && basketStatusNewish ? openedBasket : activeBasket,
);

/* Operable Basket is introduced since users can add items from a placed basket
   Taking the ID from URL is not enough anymore. That basket could be placed but active basket is different
*/
export const selectOperableBasketId = createSelector(
  selectOpenedOrActiveBasket,
  (basket) => basket?.basketId,
);

export const selectOperableDtoBasketFromEntity = createSelector(
  selectOperableBasketId,
  selectDtoBasketEntities,
  (basketId, entities) => entities[basketId as string],
);

export const selectGroupedOperableBasketsEntity = createSelector(
  selectGroupedBasketEntities,
  selectOperableBasketId,
  (entities, basketId) => (basketId ? entities[basketId] ?? null : null),
);

export const selectGroupedOperableBasketData = createSelector(
  selectGroupedOperableBasketsEntity,
  selectActiveGroupingType,
  (entity, groupType) => entity?.[groupType] ?? null,
);

export const selectOperableBasketData = createSelector(
  selectGroupedOperableBasketData,
  selectActiveSortingType,
  selectActiveSortingDirection,
  // tslint:disable-next-line:no-identical-functions
  (groupedBasket, sortType, sortDirection) => {
    return groupedBasket
      ? sortGroupedBasket(groupedBasket, sortType, sortDirection)
      : null;
  },
);

export const selectOperableBasketOrderReferenceNumber = createSelector(
  selectOperableBasketData,
  (operableBasketData) => operableBasketData?.orderReferenceNumber,
);

export const selectExtendedBasketItems = createSelector(
  selectBasketData,
  (basketData) => basketItemReducer(basketData?.groups),
);

export const selectOperableExtendedBasketItems = createSelector(
  selectOperableBasketData,
  (basketData) => basketItemReducer(basketData?.groups),
);

export const selectBasketItems = createSelector(
  selectBasketData,
  (basketData) => basketData?.basketItems || [],
);

export const selectOperableBasketItems = createSelector(
  selectOperableBasketData,
  (basketData) => basketData?.basketItems || [],
);

export const selectBasketItemIds = createSelector(
  selectBasketItems,
  (basketItems) => basketItems.map((bi) => bi.basketItemId),
);

export const selectExtendedBasketItemsMap = createSelector(
  selectExtendedBasketItems,
  (basketItems) =>
    new Map(basketItems?.map((item) => [item.basketItemId, item])),
);

export const selectBackorderBasketItems = createSelector(
  selectExtendedBasketItems,
  (items) => items.filter(isBackOrderBasketItem),
);

export const selectBackorderBasketItemsTotalQuantity = createSelector(
  selectBackorderBasketItems,
  (items) => items.reduce((acc, curr) => acc + curr.requestedQuantity, 0),
);

export const selectIsBasketFutureType = createSelector(
  selectExtendedBasketItems,
  (items) =>
    !!items.length &&
    items.every((item) => item.orderType === OrderType.Future),
);

export const selectIsBasketNowType = createSelector(
  selectExtendedBasketItems,
  (items) =>
    !!items.length && items.every((item) => item.orderType === OrderType.Now),
);

export const selectIsBasketMixed = createSelector(
  selectExtendedBasketItems,
  (items) =>
    [OrderType.Future, OrderType.Now].every((orderType) =>
      items.some((item) => item.orderType === orderType),
    ),
);

/**
 * Selects the basket id of either the active or the opened basket
 */
export const selectBasketId = createSelector(
  selectBasketData,
  (data) => data?.basketId,
);

export const selectBasketAddressesWithDeliveries = createSelector(
  selectOpenedOrActiveBasket,
  (basket) =>
    basket?.basketAccounts
      ?.filter(
        (basketAccount) =>
          basketAccount.deliveries?.length > 0 && !!basketAccount.address,
      )
      .map((basketAccount) => basketAccount.address),
);

export const selectOpenedBasketAccountsDtoWithDeliveries = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basket) =>
    basket?.basketAccounts?.filter(
      (basketAccount) =>
        basketAccount.deliveries?.length > 0 && !!basketAccount.address,
    ),
);

export const selectOpenedBasketAccountName = createSelector(
  selectOpenedBasketAccountsDtoWithDeliveries,
  (basketAccounts) =>
    basketAccounts
      ?.map((basketAccount) => basketAccount.account.name)
      .filter(isNonNullish)[0],
);

export const selectHasBasketNotAvailableAddress = createSelector(
  selectBasketAddressesWithDeliveries,
  (addresses) => !!addresses?.find((address) => isAddressDeleted(address)),
);

export const selectCanBasketBeChangedChecks = createSelector(
  selectIsBasketFrozenReviewState,
  selectIsBasketFrozenReviewStateByRule,
  selectBasketStatus,
  selectHasBasketNotAvailableAddress,
  selectIsReadOnlyDtoBasketFlag,
  (
    isFrozen,
    isFrozenByRule,
    status,
    hasDeletedAddress,
    isReadOnly,
  ): CanBasketBeChanged => {
    return {
      statusCheck: status === BasketStatus.New,
      isFrozenReviewCheck: !isFrozen,
      isFrozenReviewByRuleCheck: !isFrozenByRule,
      hasDeletedAddressCheck: !hasDeletedAddress,
      isReadOnlyCheck: !isReadOnly,
    };
  },
);

export const selectCanBasketBeChanged = createSelector(
  selectCanBasketBeChangedChecks,
  (checkValues) => Object.values(checkValues).every(Boolean),
);

export const selectCanBasketBeConditionalChanged = memoize(
  (excludedChecks: (keyof CanBasketBeChanged)[]) =>
    createSelector(selectCanBasketBeChangedChecks, (checkValues) => {
      return Object.entries(checkValues)
        .filter(
          ([key]) => !excludedChecks.includes(key as CanBasketBeChangedChecks),
        )
        .every(([_, value]) => value);
    }),
);

export const selectCannotChangeBasket = createSelector(
  selectCanBasketBeChanged,
  (canBeChanged) => !canBeChanged,
);

export const selectIsOperableStatus = createSelector(
  selectBasketDataState,
  (state) => state.isOperable,
);

export const selectIsBasketUpdateDisabled = createSelector(
  selectCannotChangeBasket,
  selectIsOperableStatus,
  (canNotBeChanged, isOperable) => canNotBeChanged || !isOperable,
);

export const selectBasketItemsForProduct = createSelector(
  selectOperableExtendedBasketItems,
  (basketItems: ExtendedBasketItem[], articleId: string) => {
    return basketItems.filter((item) => item.articleId === articleId);
  },
);

export const selectBasketItemsWithProductId = (productId: string) =>
  createSelector(
    selectExtendedBasketItems,
    (basketItems: ExtendedBasketItem[]) => {
      return basketItems.filter((item) => item.productId === productId);
    },
  );

export const selectBasketItemIdsWithTheirProductId = createSelector(
  selectExtendedBasketItems,
  (basketItems: ExtendedBasketItem[]) => {
    const basketItemIdsWithProductId: { [basketItemId: string]: string } = {};

    basketItems.forEach((item) => {
      basketItemIdsWithProductId[item.basketItemId] = item.productId;
    });

    return basketItemIdsWithProductId;
  },
);

export const selectAvailableShippingOptions = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketDto) => getShippingOptions(basketDto?.basketAccounts || []),
);

export const selectIsShippingOptionEnabled = createSelector(
  selectIsShippingOptionEnabledRule,
  selectAvailableShippingOptions,
  (isEnabled, options) => isEnabled && options.length > 0,
);

export const selectSelectedShippingOptionId = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketDto) => getSelectedShippingOptionId(basketDto?.basketAccounts || []),
);

/**
 * Selects the basket's submitted time
 */
export const selectBasketSubmittedTime = createSelector(
  selectBasketData,
  (data) => data?.submittedTime ?? null,
);

export const selectOrderReferenceNumber = createSelector(
  selectBasketData,
  (data) => data?.orderReferenceNumber ?? null,
);

export const selectMaterialsQuantities = createSelector(
  selectBasketData,
  (data) => data?.materialsQuantities ?? [],
);

export const selectCustomOrderReferenceNumber = createSelector(
  selectOpenedDtoBasketFromEntity,
  (data) => data?.clientReferenceNumber ?? null,
);

export const selectIsBasketEmpty = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketData) => {
    return Boolean(basketData && !basketData?.items.length);
  },
);

export const selectIsNoBasket = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketData) => {
    return !basketData;
  },
);

export const selectIsBasketEmptyState = createSelector(
  selectIsNoBasket,
  selectIsBasketEmpty,
  (isNoBasket, isBasketEmpty) => isNoBasket || isBasketEmpty,
);

export const selectInBasketQuantity = (product: Product) =>
  createSelector(
    selectExtendedBasketItems,
    selectMaterialsQuantities,
    (basketItems, materialsQuantities: BasketQuantity[]) => {
      return !product.parent
        ? countArticlesOccurrences(basketItems, product)
        : materialsQuantities.find(
            (entry) => entry.productId === product.productId,
          )?.amount || 0;
    },
  );

export const selectIsGroupedDeliveryOrCategory = createSelector(
  selectActiveGroupingType,
  (grouping) => {
    return (
      grouping === BasketRowsGroupType.Delivery ||
      grouping === BasketRowsGroupType.Category
    );
  },
);

export const selectAvailableGroupingOptions = createSelector(
  selectGroupByOptionsRule,
  (groupingKeys): IGroupingOption[] =>
    groupingKeys?.map((key) => ({
      actionType: key,
      displayName: basketGroupingsLocale[key],
    })) || [],
);

export const selectHasBasketShippingOptions = createSelector(
  selectBasketData,
  (basketData) => Boolean(basketData?.shippingOptions?.length),
);

export const selectShops = createSelector(
  selectBasketAddressGroupLines,
  (groups) => {
    // get unique as processed basket can has same addresses in different orderNumber groups
    return uniqueByPropertyPath(
      groups,
      'data.details.addressId',
      'data.details.accountId',
    ).map(({ data }) => data as BasketRowsGroup);
  },
);

export const selectShopsCount = createSelector(
  selectShops,
  (shops) => shops.length || 0,
);

export const selectIsBasketSdo = createSelector(
  selectShopsCount,
  (count) => count === 1,
);

export const selectIsBasketMdo = createSelector(
  selectIsBasketSdo,
  (isSdo) => !isSdo,
);

export const selectIsBasketSdoView = createSelector(
  selectIsBasketSdo,
  selectActiveGroupingType,
  (isSdo, grouping) => isSdo && grouping === BasketRowsGroupType.Article,
);

export const selectIsBasketMdoView = createSelector(
  selectIsBasketMdo,
  selectActiveGroupingType,
  (isMdo, grouping) => isMdo && grouping === BasketRowsGroupType.Article,
);

export const selectBasketNotification = createSelector(
  selectBasketDataState,
  (data) => data.basketNotification,
);

export const selectCheckDropShipOnNowBasket = createSelector(
  selectRestrictMixedBasketDropShipRule,
  selectIsBasketNowType,
  (isMixedRestricted, hasOnlyNowItems) => {
    return isMixedRestricted ? hasOnlyNowItems : true;
  },
);

export const selectIsDropShipAllowed = createSelector(
  selectIsBasketSdo,
  selectCheckDropShipOnNowBasket,
  (isSdo, isAllowedOnCurrentBasketType) =>
    isSdo && isAllowedOnCurrentBasketType,
);

export const selectCanSetDropShip = createSelector(
  selectActiveAccountDropShipAvailable,
  selectIsBasketSdo,
  selectCheckDropShipOnNowBasket,
  (isAvailable, isBasketSdo, isAllowedOnCurrentBasketType) =>
    isAvailable && isBasketSdo && isAllowedOnCurrentBasketType,
);

/**
 * Selects the basket user display name
 */
export const selectUserDisplayedName = createSelector(
  selectBasketData,
  (basketData: Basket) => basketData?.userDisplayName,
);
export const selectShipInFullOption = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basket) => Boolean(basket?.userSpecifiedGroupedDelivery),
);

export const selectAllowUserToShipInFull = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basket) => Boolean(basket?.allowUserToSpecifyShipOrderInFullForNowOrder),
);

export const selectIsShipInFullEnabled = createSelector(
  selectAllowUserToShipInFull,
  selectShipInFullEnabledRule,
  selectIsBasketNowType,
  (allowUserToShipInFull, isShipInFullEnabled, nowBasket) => {
    return Boolean(allowUserToShipInFull && nowBasket && isShipInFullEnabled);
  },
);

export const selectAlternativeAddress = createSelector(
  selectBasketData,
  (data) => data?.alternativeAddress || null,
);

export const selectIsDropShipSet = createSelector(
  selectAlternativeAddress,
  Boolean,
);

export const selectAlternativeAddressTitleType = createSelector(
  selectBasketData,
  (data) => data?.alternativeAddress?.title,
);

export const selectBasketDeliveryWindowType = createSelector(
  selectBasketData,
  (data) => data?.deliveryWindowType || DeliveryWindowType.Day,
);

export const selectBasketAssigner = createSelector(
  selectBasketData,
  (data) => data?.assigner,
);

export const selectDownloadH2Url = createSelector(
  selectDtoBasketFromEntities,
  (basket) => basket?.outputUrls?.h2,
);

export const selectAllowResendOrderConfirmation = createSelector(
  selectConfigurationsState,
  (config) => {
    return getValueFromConfigState<
      CBasketConfigurationKeys.AllowResendOrderConfirmation,
      CBasketConfigurations
    >(
      config,
      CBASKET_NAMESPACE,
      CBasketConfigurationKeys.AllowResendOrderConfirmation,
      basketConfigurationsDefaultValues[
        CBasketConfigurationKeys.AllowResendOrderConfirmation
      ],
    );
  },
);

export const selectBasketPropertyBag = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketData) => basketData?.propertyBag,
);

export const selectOperableBasketPropertyBag = createSelector(
  selectOperableDtoBasketFromEntity,
  (basketData) => basketData?.propertyBag,
);

export const selectBasketParkingOptions = createSelector(
  selectBasketPropertyBag,
  (propertyBag) => {
    return pick(
      propertyBag ?? {},
      Object.values(BasketParkingOptionProperty),
    ) as BasketParkingOptions;
  },
);

export const selectDefaultAccountPropertyBag = createSelector(
  selectAccountParkingReason,
  selectAccountParkingDate,
  selectAccountIncoterm,
  (parkingReason, parkingDate, incoterms): BasketPropertyBag => {
    return {
      [BasketParkingOptionProperty.ParkingReason]: parkingReason,
      [BasketParkingOptionProperty.ParkingDate]: parkingDate,
      shippingConditions: incoterms,
    };
  },
);

export const selectPropertyBagForAutoFill = createSelector(
  selectDefaultAccountPropertyBag,
  selectOperableBasketPropertyBag,
  (defaultBag, basketBag) => {
    return Object.fromEntries(
      Object.entries(defaultBag).filter(
        ([key]) => !basketBag?.hasOwnProperty(key),
      ),
    );
  },
);

export const selectIsBasketParkingOptionsConfigured = createSelector(
  selectBasketParkingOptions,
  (parkingOptions) => {
    return (
      Object.values(parkingOptions).filter(Boolean).length ===
      Object.keys(BasketParkingOptionProperty).length
    );
  },
);

export const selectIsBasketMerged = createSelector(
  selectBasketPropertyBag,
  (propertyBag) => !!propertyBag?.mergedFromBaskets,
);

export const selectReferencedProducts = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basketData) => basketData?.referencedProducts,
);

export const selectOperableReferencedProducts = createSelector(
  selectOperableDtoBasketFromEntity,
  (basketData) => basketData?.referencedProducts,
);

export const selectClosedOutReferencedProducts = createSelector(
  selectReferencedProducts,
  (referencedProducts) => {
    if (!referencedProducts) {
      return [];
    }

    const closedOutReferencedProducts = referencedProducts.filter((product) => {
      const isClosedOutProductAttributes =
        product.attributes.isclosedoutproduct ?? [];
      return (
        isClosedOutProductAttributes.length > 0 &&
        isClosedOutProductAttributes[0] === 'true'
      );
    });

    return closedOutReferencedProducts;
  },
);

export const selectBasketItemsWithReferenceToClosedOutProducts = createSelector(
  selectClosedOutReferencedProducts,
  selectBasketItems,
  (closedOutReferencedProducts, basketItems) => {
    if (closedOutReferencedProducts.length === 0) {
      return [];
    }

    const productIdsFromReferencedProducts = closedOutReferencedProducts.map(
      (referencedProduct) => referencedProduct.productId,
    );
    return basketItems.filter(
      (basketItem) =>
        basketItem.orderType === OrderType.Now &&
        productIdsFromReferencedProducts.includes(basketItem.productId),
    );
  },
);

export const selectFutureBasketItems = createSelector(
  selectBasketItems,
  (basketItems) => basketItems.filter(isFutureBasketItem),
);

export const selectNowBasketItems = createSelector(
  selectBasketItems,
  (basketItems) => basketItems.filter(isNowBasketItem),
);

export const selectDtoBasketItems = createSelector(
  selectOperableDtoBasketFromEntity,
  (basket) => basket?.items ?? [],
);

export const selectNowBasketTotal = createSelector(
  selectDtoBasketItems,
  (items) => {
    const nowDtoItems = items.filter(isNowDtoBasketItem) ?? [];
    return getTotalsForBasketItemIdsFromDtoBasket(nowDtoItems);
  },
);

export const selectFutureBasketTotal = createSelector(
  selectDtoBasketItems,
  (items) => {
    const futureDtoItems = items.filter(isFutureDtoBasketItem) ?? [];
    return getTotalsForBasketItemIdsFromDtoBasket(futureDtoItems);
  },
);

export const selectDeliveryShopTotals = createSelector(
  selectDtoBasketItems,
  selectBasketAddressGroupLines,
  (items, groupLines) => {
    return groupLines.map(({ basketItemIds, data }) => {
      const shopItems = items.filter((item) =>
        basketItemIds.includes(item.itemId),
      );
      return {
        now: getTotalsForBasketItemIdsFromDtoBasket(
          shopItems.filter(isNowDtoBasketItem),
        ),
        future: getTotalsForBasketItemIdsFromDtoBasket(
          shopItems.filter(isFutureDtoBasketItem),
        ),
        title: (data as BasketRowsGroup).title,
        shopId:
          ((data as BasketRowsGroup).details as BasketAddressRowsGroupDetails)
            .realAddressId ?? '',
      };
    });
  },
);

export const selectMaterialIdForBasketItems = createSelector(
  selectBasketItemIdsWithTheirProductId,
  selectReferencedProducts,
  (productIdForBasketItems, referencedProducts) => {
    const rProducts = isNonNullish(referencedProducts)
      ? referencedProducts
      : ([] as ReferencedProduct[]);
    const materialIdForBasketItem: { [basketItemId: string]: string } = {};

    Object.entries(productIdForBasketItems).forEach(
      ([basketItemId, productId]) => {
        let referencedProduct = rProducts.find(
          (product) => product.productId === productId,
        );

        if (!referencedProduct) {
          return;
        }

        if (referencedProduct.productClassId === 'size') {
          referencedProduct = rProducts.find(
            (product) =>
              product.productId === referencedProduct?.masterProductId,
          );
        }

        if (!referencedProduct) {
          return;
        }

        materialIdForBasketItem[basketItemId] = referencedProduct.productId;
      },
    );

    return materialIdForBasketItem;
  },
);

export const selectDiscountsOrderType = createSelector(
  selectOpenedDtoBasketFromEntity,
  (basket) => (basket ? getDiscountsType(basket.items) : undefined),
);

export const selectItemsInsertOrUpdate = createSelector(
  selectBasketDataState,
  (state: State) => state?.itemsInsertOrUpdate,
);

export const selectIfSomeWithoutWsp = createSelector(
  selectDtoBasketItems,
  (basketItems) =>
    basketItems.some((item) => isNullish(item.prices.grossPrice)),
);

export function getTotalsForBasketItemIdsFromDtoBasket(basketItems: Item[]) {
  const sizeTotals = getSizesTotal(basketItems);
  return aggregateTotals(sizeTotals);
}

export function getTotalForEachBasketItem(
  openedDtoBasket: ExtendedDtoBasket | undefined,
  basketItemIds: string[],
): { [basketItemId: string]: Total } {
  const totals: { [basketItemId: string]: Total } = {};

  if (!openedDtoBasket) {
    return totals;
  }

  const basketItems = openedDtoBasket.items.filter((item) =>
    basketItemIds.includes(item.itemId),
  );

  basketItems.forEach((basketItem) => {
    const total = getTotalsForBasketItemIdsFromDtoBasket([basketItem]);
    totals[basketItem.itemId] = total;
  });

  return totals;
}
